import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'

import { fetchJson, path, type QueryParams } from '@/api/client'
import type { SkelProjectBody, SkelProjectEnvelope, SkelProjectSummary } from '@/skel/types'

/**
 * Puppets: the story-facing side of the skel system (docs/SKEL-STORY.md §3.1, §3.3).
 *
 *  - `GET /api/skel/assets` — every catalog character / animal / prop, joined with its puppet link
 *    (the asset index), so the Puppets page can show "Make it move" or "Ready".
 *  - `POST /api/skel/assets/{uid}/autorig` — one click: rig that asset (a `skel.autorig` job).
 *  - `POST /api/skel/autorig-all` — the bulk job ("Make all move").
 *  - `GET /api/skel/puppets` — the wizard feed: projects usable as story cast (`uid: "skel:<id>"`).
 *  - `PATCH /api/skel/projects/{id}/meta` — the editor's Story panel (name, voice, height…).
 *
 * The routes are built by the backend track in parallel, so every reader here is lenient: a missing
 * optional field reads as "unknown", never as a crash. Query keys are local (`puppetKeys`) and all
 * start with `'skel'`, so `invalidateQueries({ queryKey: ['skel'] })` refreshes them with the rest.
 */

/* -------------------------------------------------------------------------- */
/* Shapes (§3.1 / §3.3)                                                        */
/* -------------------------------------------------------------------------- */

/** The story category of a puppet (§3.1 `meta.kind`). */
export const PUPPET_KINDS = ['person', 'animal', 'bird', 'fish', 'creature', 'object'] as const
export type PuppetKind = (typeof PUPPET_KINDS)[number]

export type PuppetPlan = 'humanoid' | 'quadruped' | 'bird' | 'fish' | 'serpent' | 'insect' | 'radial' | 'blob' | 'plant' | 'prop'
export type PuppetTier = 'A' | 'B' | 'C' | 'sheet' | 'manual'
export type AssetLinkStatus = 'queued' | 'rigging' | 'ready' | 'failed'

/** The ten story actions (§3.2), in the order the editor's checklist shows them. */
export const STORY_ACTIONS = ['idle', 'move', 'talk', 'greet', 'happy', 'sad', 'surprised', 'jump', 'sit', 'dance'] as const
export type StoryAction = (typeof STORY_ACTIONS)[number]
/** Without these three a puppet cannot act in a story (P4). */
export const REQUIRED_ACTIONS: readonly StoryAction[] = ['idle', 'move', 'talk']

/** `body.meta` of a skel project (§3.1). Every field optional on read: older projects have none. */
export interface PuppetMeta {
  asset_uid?: string | null
  kind?: PuppetKind | null
  plan?: PuppetPlan | null
  tier?: PuppetTier | null
  display_name?: string | null
  description?: string | null
  height_cm?: number | null
  voice_role?: string | null
  speaks?: boolean | null
  story_ready?: boolean | null
  human_edited?: boolean | null
  source_etag?: string | null
  pipeline_version?: number | null
  quality?: { rest_diff?: number; gates?: Record<string, boolean> } | null
}

/** What the Story panel may change (`PATCH …/meta`, partial). */
export interface PuppetMetaPatch {
  display_name?: string
  description?: string
  height_cm?: number
  voice_role?: string | null
  speaks?: boolean
  kind?: PuppetKind
}

/** One row of the asset index (`<data>/skel/assets.json`). */
export interface AssetLink {
  project_id: string | null
  plan?: PuppetPlan | null
  tier?: PuppetTier | null
  status: AssetLinkStatus
  job_id?: number | null
  error?: string | null
  updated_at?: string | null
  quality?: unknown
}

/** A row of `GET /api/skel/assets`. */
export interface CatalogPuppet {
  asset_uid: string
  name: string
  group?: string | null
  category?: string | null
  kind: PuppetKind
  /** The body plan the auto-rig uses (the server always sends it; optional for older fixtures). */
  plan?: PuppetPlan | null
  thumb_url: string | null
  link: AssetLink | null
}

export interface CatalogPuppetsPage {
  items: CatalogPuppet[]
  total: number
}

/** A row of `GET /api/skel/puppets` (the wizard feed). */
export interface PuppetFeedItem {
  /** `"skel:<ULID>"` — what a story's cast carries. */
  uid: string
  project_id: string
  name: string
  kind: PuppetKind | null
  plan?: PuppetPlan | null
  tier?: PuppetTier | null
  thumb_url: string | null
  /** Story actions this puppet can play (names; see `actionNames`). */
  actions: unknown
  speaks: boolean
  story_ready: boolean
  asset_uid?: string | null
}

/** `GET /api/skel/assets/{uid}`. */
export interface AssetPuppetState {
  asset_uid: string
  link: AssetLink | null
  project: PuppetProjectSummary | null
}

/** `SkelProjectSummary` plus the puppet fields `store.summarize` adds (§3.1). */
export type PuppetProjectSummary = SkelProjectSummary & {
  display_name?: string | null
  kind?: PuppetKind | null
  plan?: PuppetPlan | null
  tier?: PuppetTier | null
  story_ready?: boolean | null
  asset_uid?: string | null
}

export interface JobStarted {
  job_id: number
  already_running?: boolean
}

/* -------------------------------------------------------------------------- */
/* Fetchers                                                                    */
/* -------------------------------------------------------------------------- */

export interface CatalogPuppetFilters {
  kind?: PuppetKind
  status?: AssetLinkStatus
  q?: string
  limit?: number
  offset?: number
}

/** `GET /api/skel/assets?kind=&status=&q=&limit=&offset=`. */
export async function listCatalogPuppets(filters: CatalogPuppetFilters = {}, signal?: AbortSignal): Promise<CatalogPuppetsPage> {
  const page = await fetchJson<Partial<CatalogPuppetsPage>>('/api/skel/assets', { query: clean(filters), signal })
  const items = page.items ?? []
  return { items, total: page.total ?? items.length }
}

/** `GET /api/skel/assets/{uid}` — the uid travels verbatim (`char:animals/forest/fox`). */
export function getAssetPuppet(uid: string, signal?: AbortSignal): Promise<AssetPuppetState> {
  return fetchJson<AssetPuppetState>(path`/api/skel/assets/${uid}`, { signal })
}

/** `POST /api/skel/assets/{uid}/autorig?ai=&force=` → `202 {job_id}` (409-safe). */
export function autorigAsset(uid: string, opts: { ai?: boolean; force?: boolean } = {}): Promise<JobStarted> {
  return fetchJson<JobStarted>(path`/api/skel/assets/${uid}/autorig`, { method: 'POST', query: { ai: opts.ai ?? false, force: opts.force ?? false } })
}

/** `POST /api/skel/autorig-all {kinds?, ai?}` → `202 {job_id}`. */
export function autorigAll(body: { kinds?: PuppetKind[]; ai?: boolean } = {}): Promise<JobStarted> {
  return fetchJson<JobStarted>('/api/skel/autorig-all', { method: 'POST', body: { ai: false, ...body } })
}

export interface PuppetFeedFilters {
  ready?: boolean
  kind?: PuppetKind
  q?: string
}

/**
 * `GET /api/skel/puppets?ready=1|0&kind=&q=`. The server defaults to `ready=1`, so `ready` is always
 * sent: without `ready: true` every puppet comes back (the Puppets page's "Needs moves" badge, the
 * Story panel of a puppet that cannot act yet and the picker's disabled tiles all need those).
 */
export async function listPuppets(filters: PuppetFeedFilters = {}, signal?: AbortSignal): Promise<{ items: PuppetFeedItem[] }> {
  const query: QueryParams = { kind: filters.kind, q: filters.q?.trim() || undefined, ready: filters.ready ? 1 : 0 }
  const page = await fetchJson<{ items?: PuppetFeedItem[] }>('/api/skel/puppets', { query, signal })
  return { items: page.items ?? [] }
}

/**
 * `PATCH /api/skel/projects/{id}/meta` (partial) → the whole envelope, `rev` bumped. With `baseRev`
 * a stale rev is a 409 `{current_rev}` and nothing is written (the skeleton editor's Save).
 */
export function patchPuppetMeta(projectId: string, patch: PuppetMetaPatch, opts: { baseRev?: number } = {}): Promise<SkelProjectEnvelope> {
  const body = opts.baseRev === undefined ? patch : { ...patch, base_rev: opts.baseRev }
  return fetchJson<SkelProjectEnvelope>(path`/api/skel/projects/${projectId}/meta`, { method: 'PATCH', body })
}

/* -------------------------------------------------------------------------- */
/* Keys + query options                                                        */
/* -------------------------------------------------------------------------- */

export const puppetKeys = {
  all: () => ['skel'] as const,
  catalog: (f: object = {}) => ['skel', 'catalog', clean(f)] as const,
  asset: (uid: string) => ['skel', 'asset', uid] as const,
  feed: (f: object = {}) => ['skel', 'puppets', clean(f)] as const,
  objects: (q: string, limit: number) => ['skel', 'objects', q, limit] as const,
}

/**
 * Rows one object lookup brings: every match of a common noun, so the caller can rank them itself
 * (the server matches substrings of name, category and uid: `ball` → 17 rows, `Crystal Ball` first).
 */
export const OBJECT_LOOKUP_LIMIT = 40

/** `GET /api/skel/assets?kind=object&q=` — catalog objects whose name, category or uid contain `q`. */
export function searchObjects(q: string, signal?: AbortSignal, limit = OBJECT_LOOKUP_LIMIT): Promise<CatalogPuppetsPage> {
  return listCatalogPuppets({ kind: 'object', q: q.trim() || undefined, limit }, signal)
}

/** One object lookup, cached by its word for the session (the wizard asks the same nouns again as the idea is edited). */
export const objectSearchQuery = (q: string, limit = OBJECT_LOOKUP_LIMIT) =>
  queryOptions({
    queryKey: puppetKeys.objects(q.trim().toLowerCase(), limit),
    queryFn: ({ signal }) => searchObjects(q, signal, limit),
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
    retry: false,
  })

/** How many catalog cards one page brings (the server may cap lower). */
export const CATALOG_PAGE = 60

/**
 * Infinite catalog list over one or more kinds, one after the other ("Animals" = animal then
 * creature; `[undefined]` = every kind at once). The page param is `{k, offset}`: the kind index
 * and the offset inside it.
 */
export const catalogPuppetsInfiniteQuery = (kinds: readonly (PuppetKind | undefined)[], q = '', limit = CATALOG_PAGE) =>
  infiniteQueryOptions({
    queryKey: puppetKeys.catalog({ kinds: kinds.map((k) => k ?? 'all').join(','), q: q.trim(), limit }),
    queryFn: ({ pageParam, signal }) => listCatalogPuppets({ kind: kinds[pageParam.k], q: q.trim() || undefined, limit, offset: pageParam.offset }, signal),
    initialPageParam: { k: 0, offset: 0 },
    getNextPageParam: (last, _all, param) => {
      const next = param.offset + last.items.length
      if (last.items.length > 0 && next < last.total) return { k: param.k, offset: next }
      if (param.k + 1 < kinds.length) return { k: param.k + 1, offset: 0 }
      return undefined
    },
    staleTime: 10_000,
    placeholderData: (prev) => prev,
  })

export const puppetsQuery = (filters: PuppetFeedFilters = {}) =>
  queryOptions({
    queryKey: puppetKeys.feed(filters),
    queryFn: ({ signal }) => listPuppets(filters, signal),
    staleTime: 10_000,
    retry: false,
  })

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

function clean(f: object): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {}
  for (const [k, v] of Object.entries(f)) {
    if (v === undefined || v === null || v === '') continue
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') out[k] = v
  }
  return out
}

/** The cast uid of a skel project. */
export const skelUid = (projectId: string): string => `skel:${projectId}`

/** The project id inside a `skel:<id>` uid, else null. */
export function projectIdOfUid(uid: string): string | null {
  return uid.startsWith('skel:') ? uid.slice(5) || null : null
}

/**
 * The action names of a feed item, whatever shape the server chose: `["idle", …]`,
 * `[{name|action: "idle", …}]`, or the `{idle: [...], …}` map of `puppet_actions`.
 */
export function actionNames(actions: unknown): string[] {
  if (Array.isArray(actions)) {
    return actions
      .map((a) => (typeof a === 'string' ? a : a && typeof a === 'object' ? String((a as { name?: unknown; action?: unknown }).name ?? (a as { action?: unknown }).action ?? '') : ''))
      .filter((a) => a.length > 0)
  }
  if (actions && typeof actions === 'object') return Object.keys(actions)
  return []
}

/** `body.meta` of a project body, or null (the frontend type gains `meta` in the A2 track). */
export function readPuppetMeta(body: SkelProjectBody | null | undefined): PuppetMeta | null {
  const meta = (body as { meta?: unknown } | null | undefined)?.meta
  return meta && typeof meta === 'object' ? (meta as PuppetMeta) : null
}

/** Default "speaks" for a kind (§3.1: objects are quiet unless told otherwise). */
export const defaultSpeaks = (kind: PuppetKind | null | undefined): boolean => kind !== 'object'

/** What a card or a picker tile shows. */
export type PuppetStatus = 'ready' | 'needs_moves' | 'rigging' | 'failed' | 'none'

export const STATUS_LABEL: Record<PuppetStatus, string> = {
  ready: 'Ready',
  needs_moves: 'Needs moves',
  rigging: 'Learning to move…',
  failed: 'Didn’t work',
  none: 'Not moving yet',
}

/**
 * A catalog card's status: the server's index entry, refined by the puppets feed (a linked project
 * that is not story-ready "needs moves"). `live` is a job this tab started and is still watching.
 */
export function catalogStatus(item: Pick<CatalogPuppet, 'link'>, opts: { live?: 'rigging' | 'failed' | 'ready' | null; storyReady?: boolean | null } = {}): PuppetStatus {
  if (opts.live === 'rigging') return 'rigging'
  const link = item.link
  if (opts.live === 'ready' && !link) return 'ready'
  if (!link) return opts.live === 'failed' ? 'failed' : 'none'
  if (link.status === 'queued' || link.status === 'rigging') return opts.live === 'failed' ? 'failed' : 'rigging'
  if (link.status === 'failed') return 'failed'
  if (opts.live === 'failed') return 'failed'
  return opts.storyReady === false ? 'needs_moves' : 'ready'
}

/** Friendly word for a kind (tabs, pickers, the Story panel). */
export const KIND_WORD: Record<PuppetKind, string> = {
  person: 'Person',
  animal: 'Animal',
  bird: 'Bird',
  fish: 'Sea creature',
  creature: 'Creature',
  object: 'Prop',
}
